import { type AttachmentCategory, type AttachmentSelectDto, type AttachmentType } from '@@types/api.types';
import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import type { AttachmentTargetType, BufferedAttachment } from '@queries/attachments.query';
import { FunctionComponent, useState } from 'react';
import { AttachmentsList } from './attachments-list.component';
import { AttachmentsModal } from './attachments-modal.component';
import { BufferedAttachmentsList } from './buffered-attachments-list.component';

type AttachmentsSectionProps = {
  attachments?: AttachmentSelectDto[];
  bufferedAttachments?: BufferedAttachment[];
  canEdit?: boolean;
  category: AttachmentCategory;
  className?: string;
  defaultType?: AttachmentType;
  isAttachmentsPending?: boolean;
  onBufferEdit?: (clientId: string, data: BufferedAttachment) => void;
  onBufferRemove?: (clientId: string) => void;
  onBufferSave?: (data: BufferedAttachment) => void;
  showHeader?: boolean;
  targetId?: string;
  targetType?: AttachmentTargetType;
  title?: string;
  useFormLabel?: boolean;
};

export const AttachmentsSection: FunctionComponent<AttachmentsSectionProps> = (props) => {
  const {
    attachments: apiAttachments = [],
    bufferedAttachments,
    canEdit = true,
    category,
    className,
    defaultType,
    isAttachmentsPending: isApiPending = false,
    onBufferEdit,
    onBufferRemove,
    onBufferSave,
    showHeader = true,
    targetId,
    targetType = 'technology',
    title = 'Attachments',
    useFormLabel = false,
  } = props;

  const isBufferMode = !targetId && Boolean(onBufferSave);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [attachmentToEdit, setAttachmentToEdit] = useState<AttachmentSelectDto | null>(null);
  const [bufferedAttachmentToEdit, setBufferedAttachmentToEdit] = useState<BufferedAttachment | null>(null);

  const handleOpenModal = (attachment?: AttachmentSelectDto) => {
    setAttachmentToEdit(attachment ?? null);
    setBufferedAttachmentToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenBufferedModal = (buffered?: BufferedAttachment) => {
    setBufferedAttachmentToEdit(buffered ?? null);
    setAttachmentToEdit(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setAttachmentToEdit(null);
    setBufferedAttachmentToEdit(null);
    setIsModalOpen(false);
  };

  const handleBufferSave = (data: BufferedAttachment) => {
    if (bufferedAttachmentToEdit && onBufferEdit) {
      onBufferEdit(bufferedAttachmentToEdit.clientId, data);
    } else if (onBufferSave) {
      onBufferSave(data);
    }
  };

  const handleBufferRemove = (clientId: string) => {
    onBufferRemove?.(clientId);
    handleCloseModal();
  };

  // Determine which attachments to show
  const attachments = isBufferMode ? [] : apiAttachments;
  const buffered = isBufferMode ? (bufferedAttachments ?? []) : [];
  const totalCount = attachments.length + buffered.length;
  const isEmpty = totalCount === 0;

  return (
    <section className={cn('flex w-full flex-col gap-2', className)}>
      {showHeader && (
        <div className="flex items-center justify-between">
          {useFormLabel ? (
            <FormLabel
              label={title}
              tooltip="Patents, Research Papers, etc."
            />
          ) : (
            <h3 className="text-sm font-medium text-gray-600">{title}</h3>
          )}
        </div>
      )}

      {/* API-driven list (EDIT mode) */}
      {!isBufferMode && !isEmpty && (
        <AttachmentsList
          attachments={attachments}
          onEdit={canEdit ? handleOpenModal : undefined}
          showHeader={false}
          useFormLabel={false}
        />
      )}

      {/* Buffered list (CREATE mode) */}
      {isBufferMode && buffered.length > 0 && (
        <BufferedAttachmentsList
          attachments={buffered}
          onEdit={canEdit ? handleOpenBufferedModal : undefined}
          onRemove={canEdit ? handleBufferRemove : undefined}
        />
      )}

      {/* Loading state */}
      {!isBufferMode && isApiPending && <div className="text-sm text-gray-400">Loading attachments...</div>}

      {/* Empty state + Add button */}
      {canEdit && (
        <Button
          className="h-auto self-start"
          onClick={() => (isBufferMode ? handleOpenBufferedModal() : handleOpenModal())}
          type="button"
          variant="link"
        >
          + Add Research Artefact
        </Button>
      )}

      {/* Modal */}
      <AttachmentsModal
        attachmentToEdit={attachmentToEdit}
        bufferedAttachmentToEdit={bufferedAttachmentToEdit}
        category={category}
        defaultType={defaultType}
        isOpen={isModalOpen}
        onBufferRemove={isBufferMode ? handleBufferRemove : undefined}
        onBufferSave={isBufferMode ? handleBufferSave : undefined}
        onClose={handleCloseModal}
        targetId={targetId}
        targetType={targetType}
      />
    </section>
  );
};
